import { useState } from "react";
import { Zap, Plus, X, Bike } from "lucide-react";
import { api } from "../lib/api";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";

const AddItemRow = ({ onAdd, testid }) => {
  const [val, setVal] = useState("");
  const submit = () => {
    if (!val.trim()) return;
    onAdd(val.trim());
    setVal("");
  };
  return (
    <div className="flex items-center gap-2 mt-4">
      <input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Item toevoegen..." maxLength={100}
        className="flex-1 min-w-0 bg-secondary rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-primary transition-[box-shadow] duration-200" />
      <button data-testid={`${testid}-button`} onClick={submit} disabled={!val.trim()}
        className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 shrink-0 active:scale-95 transition-[transform] duration-200">
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
};

export const PackingList = ({ trip, onUpdate }) => {
  const [list, setList] = useState(trip.packing_list);

  if (!list) return <p className="text-muted-foreground text-sm">Nog geen paklijst beschikbaar.</p>;

  const allItems = [...list.categories.flatMap((c) => c.items), ...list.ev_checklist];
  const checkedCount = allItems.filter((i) => i.checked).length;
  const pct = allItems.length ? Math.round((checkedCount / allItems.length) * 100) : 0;

  const toggle = async (section, catIdx, itemIdx, checked) => {
    const updated = structuredClone(list);
    if (section === "ev") updated.ev_checklist[itemIdx].checked = checked;
    else updated.categories[catIdx].items[itemIdx].checked = checked;
    setList(updated);
    onUpdate?.(updated);
    await api.patch(`/trips/${trip.id}/packing`, { section, category_index: catIdx, item_index: itemIdx, checked }).catch(() => {});
  };

  const addItem = async (section, catIdx, text) => {
    const updated = structuredClone(list);
    const item = { text, checked: false };
    if (section === "ev") updated.ev_checklist.push(item);
    else updated.categories[catIdx].items.push(item);
    setList(updated);
    onUpdate?.(updated);
    await api.post(`/trips/${trip.id}/packing/items`, { section, category_index: catIdx, text }).catch(() => {});
  };

  const removeItem = async (section, catIdx, itemIdx) => {
    const updated = structuredClone(list);
    if (section === "ev") updated.ev_checklist.splice(itemIdx, 1);
    else updated.categories[catIdx].items.splice(itemIdx, 1);
    setList(updated);
    onUpdate?.(updated);
    await api.delete(`/trips/${trip.id}/packing/items`, { params: { section, category_index: catIdx, item_index: itemIdx } }).catch(() => {});
  };

  const ItemRow = ({ item, section, ci, ii, testid }) => (
    <div data-testid={testid} className="flex items-center gap-3 group">
      <Checkbox checked={item.checked} onCheckedChange={(c) => toggle(section, ci, ii, !!c)} />
      <span onClick={() => toggle(section, ci, ii, !item.checked)}
        className={`flex-1 min-w-0 text-sm cursor-pointer transition-[color] duration-200 ${item.checked ? "line-through text-muted-foreground" : "group-hover:text-primary"}`}>
        {item.text}
      </span>
      <button data-testid={`${testid}-delete`} onClick={() => removeItem(section, ci, ii)} title="Item verwijderen"
        className="opacity-50 sm:opacity-0 sm:group-hover:opacity-100 p-1 rounded-full hover:bg-red-50 text-red-400 shrink-0 transition-[opacity,background-color] duration-200">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="packing-list">
      <div className="bg-card rounded-2xl p-5">
        <div className="flex justify-between items-center mb-2">
          <p className="font-heading font-bold">Inpak-voortgang</p>
          <span className="font-mono text-sm text-primary font-semibold">{checkedCount}/{allItems.length}</span>
        </div>
        <Progress value={pct} className="h-2" />
      </div>

      {list.categories.map((cat, ci) => (
        <div key={cat.name} className="bg-card rounded-2xl p-6">
          <h3 className="font-heading font-bold mb-4">{cat.name}</h3>
          <div className="space-y-3">
            {cat.items.map((item, ii) => (
              <ItemRow key={`${ii}-${item.text}`} item={item} section="category" ci={ci} ii={ii} testid={`packing-item-${ci}-${ii}`} />
            ))}
          </div>
          <AddItemRow onAdd={(text) => addItem("category", ci, text)} testid={`packing-add-${ci}`} />
        </div>
      ))}

      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6">
        <h3 className="font-heading font-bold mb-4 flex items-center gap-2 text-primary">
          {trip.travel_mode === "moto" ? <Bike className="w-4 h-4" /> : <Zap className="w-4 h-4" />} {trip.travel_mode === "moto" ? "Moto-checklist" : "EV-checklist"}
        </h3>
        <div className="space-y-3">
          {list.ev_checklist.map((item, ii) => (
            <ItemRow key={`${ii}-${item.text}`} item={item} section="ev" ci={0} ii={ii} testid={`packing-ev-item-${ii}`} />
          ))}
        </div>
        <AddItemRow onAdd={(text) => addItem("ev", 0, text)} testid="packing-add-ev" />
      </div>
    </div>
  );
};
